import React, { useMemo, useState } from 'react';

import { EmailDuplicateDialog } from '@components/admin/members/EmailDuplicateDialog';
import { MembershipTextInput } from '@components/admin/members/MembershipFormDetails/Forms';
import { Button } from '@components/Button';
import { SpinnerModal } from '@components/SpinnerModal';
import { usePatchCompanyPrincipal } from '@hooks/mutation/dashboard/usePatchCompanyPrincipal';
import { useSearch } from '@hooks/useSearch';
import { MembershipFormFields, PrincipalForm } from '@models/MembershipForm';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { EmailDuplicateError, FormError } from '@utils/errors';
import { createValidate, formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import clsx from 'clsx';
import { Form, Formik } from 'formik';
import { isRight } from 'fp-ts/lib/Either';
import * as t from 'io-ts';
import { Prompt } from 'react-router-dom';
import * as h from 'tyrann-io';

const strings = createStrings({
  ja: {
    save: '保存',
    title: 'D2D会員情報の変更',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  formContainer: {
    ...common.flexColumn,
    marginBottom: '1rem',
  },
  formButton: {
    width: 70,
    margin: '1.5rem 1rem',
  },
}));

const searchType = t.type({
  id: h.number().cast(),
});

interface PrincipalFormProps {
  initialValues: PrincipalForm;
  onFinish: () => void;
}

export const PrincipalFormik: React.FC<PrincipalFormProps> = (props) => {
  const { onFinish, initialValues } = props;

  const schema = useMemo(() => MembershipFormFields.shared.toPrincipalFormSchema(), []);
  const validate = useMemo(() => createValidate(schema), [schema]);

  const { search } = useSearch(searchType);

  const patchPrincipal = usePatchCompanyPrincipal(search.id);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  return (
    <Formik
      validate={validate}
      initialValues={initialValues}
      validateOnMount
      onSubmit={async (values) => {
        const validation = schema.decode(values);

        if (!isRight(validation)) {
          throw new Error('PrincipalFormik: Unexpected erroneus form values. ');
        }

        const patchPrincipalData = MembershipFormFields.shared.mapPrincipalFormToPatchPrincipal(
          validation.right,
        );

        try {
          await patchPrincipal.mutateAsync(patchPrincipalData);
          onFinish();
        } catch (e) {
          if (e instanceof EmailDuplicateError) {
            setEmailDialogOpen(true);
          } else if (e instanceof FormError) {
            // eslint-disable-next-line no-alert
            alert(e.getDisplayMessage());
          } else {
            alert(String(e));
          }
          throw e;
        }
      }}
    >
      {({ touched, isSubmitting }) => (
        <Form className={clsx(classes.formContainer, classes.verticalSpace1)}>
          <EmailDuplicateDialog open={emailDialogOpen} onClose={() => setEmailDialogOpen(false)} />
          <Prompt
            when={Object.keys(touched).length > 0 && !isSubmitting}
            message={() => formStrings.promptLeaving}
          />

          <SpinnerModal open={patchPrincipal.isLoading} />

          <MembershipTextInput field={MembershipFormFields.shared.principalLastName} />
          <MembershipTextInput field={MembershipFormFields.shared.principalFirstName} />
          <MembershipTextInput field={MembershipFormFields.shared.principalCellphone} />
          <MembershipTextInput field={MembershipFormFields.shared.principalLandline} />
          <MembershipTextInput field={MembershipFormFields.shared.principalEmail} />
          <Button
            color="primary"
            shape="round"
            className={classes.formButton}
            text={strings.save}
          />
        </Form>
      )}
    </Formik>
  );
};
